import { BookOpen, Lock, MessageSquare, Paperclip, Send, Smile, X } from 'lucide-react';
import {
  useId,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import type { ChatShortcut, HelpArticle } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { Spinner } from '@nucleus-commerce/ui/components/spinner';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';
import { ToggleGroup, ToggleGroupItem } from '@nucleus-commerce/ui/components/toggle-group';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { conversationsApi } from '../api/conversations-api';
import { useChatShortcuts } from '../hooks/use-chat-shortcuts';
import { useSendMessage, useUploadAttachment } from '../hooks/use-conversation-mutations';
import { useHelpArticleSearch } from '../hooks/use-help-article-search';
import { useInboxLive } from '../hooks/use-inbox-live';
import {
  articleLink,
  articleQuery,
  MIN_ARTICLE_QUERY,
  replaceArticleQuery,
} from '../lib/article-query';
import { shortcutQuery, replaceShortcutQuery } from '../lib/shortcut-query';

import { JewelleryActions } from './jewellery-actions';

const MAX_BODY = 4000;
const MAX_FILES = 5;
const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';
const EMOJI = [
  '🙂',
  '😊',
  '😀',
  '🙏',
  '👍',
  '👌',
  '🎉',
  '💍',
  '💎',
  '✨',
  '❤️',
  '🤍',
  '📦',
  '🚚',
  '📅',
  '✅',
];

interface Attached {
  token: string;
  filename: string;
}

export interface ComposerProps {
  storeId: string;
  conversationId: string;
  /** Name used in the placeholder ("Reply to Maya…"). */
  shopper: string;
  /** The conversation's latest quote (context.draftOrderId). */
  draftOrderId?: string | null;
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
}

/**
 * Reply or whisper. `/` opens the shortcut palette (placeholders are filled by the backend for this
 * conversation), `?words` searches the help centre and inserts a live article as a markdown link,
 * files attach by button, drop or paste, Ctrl+Enter sends. A failed send keeps the
 * draft; the global error toast explains why.
 */
export function Composer({
  storeId,
  conversationId,
  shopper,
  draftOrderId = null,
  textareaRef,
}: ComposerProps) {
  const { t } = useTranslation('conversations');
  const live = useInboxLive();
  const send = useSendMessage(storeId, conversationId);
  const upload = useUploadAttachment(storeId, conversationId);
  const shortcuts = useChatShortcuts(storeId);
  const [body, setBody] = useState('');
  const [mode, setMode] = useState<'reply' | 'whisper'>('reply');
  const [files, setFiles] = useState<Attached[]>([]);
  const [caret, setCaret] = useState(0);
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [inserting, setInserting] = useState(false);
  const lastTyping = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const ownRef = useRef<HTMLTextAreaElement>(null);
  const textarea = textareaRef ?? ownRef;
  const listId = useId();
  const whisper = mode === 'whisper';

  const query = shortcutQuery(body, caret);
  const matches = useMemo<ChatShortcut[]>(() => {
    if (query === null) return [];
    const needle = query.toLowerCase();
    return (shortcuts.data ?? [])
      .filter((s) => s.key.toLowerCase().includes(needle) || s.title.toLowerCase().includes(needle))
      .slice(0, 8);
  }, [query, shortcuts.data]);
  const shortcutsOpen = query !== null && query !== dismissed && matches.length > 0;

  const helpQuery = query === null ? articleQuery(body, caret) : null;
  const help = useHelpArticleSearch(storeId, helpQuery);
  const articles: Array<HelpArticle & { url: string }> =
    helpQuery !== null && helpQuery.trim().length >= MIN_ARTICLE_QUERY ? (help.data ?? []) : [];
  const articlesOpen =
    !shortcutsOpen && helpQuery !== null && helpQuery !== dismissed && articles.length > 0;
  const paletteOpen = shortcutsOpen || articlesOpen;
  const paletteSize = shortcutsOpen ? matches.length : articles.length;

  const typing = () => {
    const now = Date.now();
    if (now - lastTyping.current < 1_000) return;
    lastTyping.current = now;
    live.sendTyping(conversationId, whisper);
  };

  const insertShortcut = async (shortcut: ChatShortcut) => {
    setInserting(true);
    try {
      const rendered = await conversationsApi.renderShortcut(storeId, conversationId, shortcut.id);
      const next = replaceShortcutQuery(body, caret, rendered.body);
      setBody(next.text);
      requestAnimationFrame(() => {
        textarea.current?.focus();
        textarea.current?.setSelectionRange(next.caret, next.caret);
        setCaret(next.caret);
      });
    } finally {
      setInserting(false);
    }
  };

  const insertArticle = (article: HelpArticle & { url: string }) => {
    const next = replaceArticleQuery(body, caret, articleLink(article));
    setBody(next.text);
    requestAnimationFrame(() => {
      textarea.current?.focus();
      textarea.current?.setSelectionRange(next.caret, next.caret);
      setCaret(next.caret);
    });
  };

  const addFiles = (list: FileList | File[]) => {
    const room = MAX_FILES - files.length;
    for (const file of Array.from(list).slice(0, Math.max(0, room))) {
      upload.mutate(file, {
        onSuccess: (uploaded) =>
          setFiles((current) => [
            ...current,
            { token: uploaded.token, filename: uploaded.filename },
          ]),
      });
    }
  };

  const text = body.trim();
  const canSend =
    (text.length > 0 || files.length > 0) &&
    text.length <= MAX_BODY &&
    !send.isPending &&
    !upload.isPending;

  const submit = () => {
    if (!canSend) return;
    send.mutate(
      {
        ...(text ? { body: text } : {}),
        ...(files.length > 0 ? { attachments: files.map((file) => file.token) } : {}),
        ...(whisper ? { isInternal: true } : {}),
      },
      {
        onSuccess: () => {
          setBody('');
          setFiles([]);
          setCaret(0);
        },
      },
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (paletteOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setActive((current) => (current + step + paletteSize) % paletteSize);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const index = Math.min(active, paletteSize - 1);
        if (shortcutsOpen) {
          const chosen = matches[index];
          if (chosen) void insertShortcut(chosen);
        } else {
          const chosen = articles[index];
          if (chosen) insertArticle(chosen);
        }
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setDismissed(shortcutsOpen ? query : helpQuery);
        return;
      }
    }
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      submit();
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    if (event.dataTransfer.files.length === 0) return;
    event.preventDefault();
    addFiles(event.dataTransfer.files);
  };

  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    if (event.clipboardData.files.length === 0) return;
    event.preventDefault();
    addFiles(event.clipboardData.files);
  };

  return (
    <div
      className={cn('relative border-t p-3', whisper && 'bg-warning/10')}
      onDragOver={(event) => event.preventDefault()}
      onDrop={onDrop}
    >
      {articlesOpen ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={t('composer.articles')}
          aria-busy={help.isFetching}
          className="bg-popover text-popover-foreground absolute right-3 bottom-full left-3 z-20 mb-1 max-h-64 overflow-y-auto rounded-md border p-1 shadow-md"
        >
          {articles.map((article, index) => (
            <li
              key={article.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className={cn(
                'cursor-pointer rounded-sm px-2 py-1.5 text-sm',
                index === active && 'bg-accent text-accent-foreground',
              )}
              onMouseDown={(event) => {
                event.preventDefault();
                insertArticle(article);
              }}
              onMouseEnter={() => setActive(index)}
            >
              <span className="flex items-center gap-1.5">
                <BookOpen className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
                <span className="truncate font-medium">{article.title}</span>
                <span className="text-muted-foreground ml-auto shrink-0 text-xs">
                  {article.category?.name ?? t('composer.articleNoCategory')}
                </span>
              </span>
              {article.summary ? (
                <span className="text-muted-foreground block truncate text-xs">
                  {article.summary}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {shortcutsOpen ? (
        <ul
          id={listId}
          role="listbox"
          aria-label={t('composer.shortcuts')}
          className="bg-popover text-popover-foreground absolute right-3 bottom-full left-3 z-20 mb-1 max-h-64 overflow-y-auto rounded-md border p-1 shadow-md"
        >
          {matches.map((shortcut, index) => (
            <li
              key={shortcut.id}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className={cn(
                'cursor-pointer rounded-sm px-2 py-1.5 text-sm',
                index === active && 'bg-accent text-accent-foreground',
              )}
              onMouseDown={(event) => {
                event.preventDefault();
                void insertShortcut(shortcut);
              }}
              onMouseEnter={() => setActive(index)}
            >
              <span className="font-mono text-xs">/{shortcut.key}</span>
              <span className="text-muted-foreground ml-2">{shortcut.title}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mb-2 flex items-center gap-2">
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={mode}
          onValueChange={(value) => {
            if (value === 'reply' || value === 'whisper') setMode(value);
          }}
          aria-label={t('composer.mode')}
        >
          <ToggleGroupItem value="reply" className="gap-1 px-2.5 text-xs">
            <MessageSquare className="size-3.5" aria-hidden />
            {t('composer.reply')}
          </ToggleGroupItem>
          <ToggleGroupItem value="whisper" className="gap-1 px-2.5 text-xs">
            <Lock className="size-3.5" aria-hidden />
            {t('composer.whisper')}
          </ToggleGroupItem>
        </ToggleGroup>
        {whisper ? (
          <span className="text-muted-foreground text-xs">{t('composer.whisperHint')}</span>
        ) : null}
      </div>
      <Textarea
        ref={textarea}
        value={body}
        rows={3}
        maxLength={MAX_BODY}
        aria-label={
          whisper ? t('composer.whisperLabel') : t('composer.replyLabel', { name: shopper })
        }
        placeholder={
          whisper ? t('composer.whisperPlaceholder') : t('composer.placeholder', { name: shopper })
        }
        aria-controls={paletteOpen ? listId : undefined}
        aria-activedescendant={paletteOpen ? `${listId}-${active}` : undefined}
        aria-autocomplete="list"
        className="max-h-48 resize-none"
        onChange={(event) => {
          setBody(event.target.value);
          setCaret(event.target.selectionStart);
          setActive(0);
          setDismissed(null);
          typing();
        }}
        onSelect={(event) => setCaret(event.currentTarget.selectionStart)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
      />
      {files.length > 0 || upload.isPending ? (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {files.map((file) => (
            <li
              key={file.token}
              className="bg-muted flex items-center gap-1 rounded-md px-2 py-1 text-xs"
            >
              <span className="max-w-40 truncate">{file.filename}</span>
              <button
                type="button"
                aria-label={t('composer.removeFile', { name: file.filename })}
                onClick={() => setFiles((current) => current.filter((f) => f.token !== file.token))}
              >
                <X className="size-3" aria-hidden />
              </button>
            </li>
          ))}
          {upload.isPending ? (
            <li className="flex items-center gap-1 px-2 py-1 text-xs">
              <Spinner />
              {t('composer.uploading')}
            </li>
          ) : null}
        </ul>
      ) : null}
      <div className="mt-2 flex items-center gap-1">
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(event) => {
            if (event.target.files) addFiles(event.target.files);
            event.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={t('composer.attach')}
          disabled={files.length >= MAX_FILES}
          onClick={() => fileInput.current?.click()}
        >
          <Paperclip aria-hidden />
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label={t('composer.emoji')}
            >
              <Smile aria-hidden />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="grid w-auto grid-cols-8 gap-1 p-2">
            {EMOJI.map((emoji) => (
              <button
                key={emoji}
                type="button"
                className="hover:bg-accent rounded p-1 text-lg leading-none"
                onClick={() => {
                  const next = body.slice(0, caret) + emoji + body.slice(caret);
                  setBody(next);
                  setCaret(caret + emoji.length);
                }}
              >
                {emoji}
              </button>
            ))}
          </PopoverContent>
        </Popover>
        <JewelleryActions
          storeId={storeId}
          conversationId={conversationId}
          draftOrderId={draftOrderId}
          disabled={whisper || send.isPending}
        />
        {inserting ? <Spinner /> : null}
        <span className="text-muted-foreground ml-auto hidden text-xs sm:inline">
          {t('composer.sendHint')}
        </span>
        <Button type="button" size="sm" onClick={submit} disabled={!canSend}>
          {send.isPending ? <Spinner /> : <Send aria-hidden />}
          {whisper ? t('composer.addNote') : t('composer.send')}
        </Button>
      </div>
    </div>
  );
}
